/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import classNames from 'classnames';
import type { FormikErrors, FormikTouched } from 'formik';

import messages from './HTMLField.messages';
import AuroraHTMLEditor from './AuroraHTMLEditor';
import { useFormattedMessages } from '@broadleaf/admin-components/dist/common';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import { FieldDecorations } from '@broadleaf/admin-components/dist/form';
import { IMetadataFieldComponent } from '@broadleaf/admin-components/dist/types/metadata';
import { IFormik } from '@broadleaf/admin-components/dist/types/form';
import { mustHaveMetadataProps } from '@broadleaf/admin-components/dist/form/utils/invariantHelpers';
import { get } from 'lodash';
import { unstable } from '@broadleaf/admin-components';
const { MetadataUtils } = unstable;
const { isFieldDisabled } = MetadataUtils;

/**
 * Renders a rich text editor for the HTML field type.
 *
 */

export const AuroraHTMLField: React.FC<HTMLFieldContainerProps> = props => {
  mustHaveMetadataProps(props.metadata, ['name']);

  const handleChange = value => {
    const { metadata, formik } = props;
    formik.setFieldValue(metadata.name, value);
  };

  const { disabled, metadata, formik } = props;
  const error = get(formik.errors, metadata.name);
  const isTouched = get(formik.touched, metadata.name, false);
  const value = get(formik.values, metadata.name);
  return (
    <HTMLFieldInternal
      disabled={disabled}
      error={error}
      metadata={metadata}
      isTouched={isTouched}
      onChange={handleChange}
      onBlur={formik.handleBlur}
      value={value}
      {...props}
    />
  );
};

const HTMLFieldInternal: React.FC<HTMLFieldProps> = props => {
  const { error, metadata, onChange, onBlur, isTouched, value } = props;
  const { name } = metadata;
  const translateMode = useTranslateMode();
  const { editorLabel, htmlLabel } = useFormattedMessages(messages);
  return (
    <FieldDecorations fullWidth {...props}>
      <AuroraHTMLEditor
        className={classNames({
          'is-invalid': isTouched && !!error
        })}
        editorClassName={classNames('HTMLField', {
          'form-control': true,
          'html-form-control': true,
          'is-invalid': isTouched && !!error
        })}
        editorLabel={editorLabel}
        htmlLabel={htmlLabel}
        name={name}
        wrapperId={name}
        disabled={isFieldDisabled(props, translateMode)}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
      />
    </FieldDecorations>
  );
};

AuroraHTMLField.defaultProps = {
  metadata: {
    readOnly: false,
    required: false
  }
};

export interface HTMLFieldProps {
  disabled?: boolean;
  metadata: IMetadataFieldComponent;
  onChange?: (value: string) => void;
  value?: string;
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  isTouched?: boolean | FormikTouched<any> | FormikTouched<any>[];
  formik: IFormik;
  onBlur?: React.FocusEventHandler;
}

export interface HTMLFieldContainerProps {
  disabled?: boolean;
  formik: IFormik;
  metadata: IMetadataFieldComponent;
}

export default AuroraHTMLField;
